import { useState, type FormEvent } from 'react';
import { useContentReport, useSimulate, useTemplates } from '../api/hooks';
import { SIMULATION_PATH_CAP, TIERS, type ContentReport, type Gender, type SimulationResult, type TemplateStats } from '../api/types';
import { PageHeader } from '../app/Shell';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  ErrorNote,
  Field,
  JsonView,
  KeyValues,
  Meter,
  QueryView,
  Section,
  Segmented,
  fmtInt,
  fmtPct,
  humanize,
  seriesColor,
  type Tone,
} from '../ui';

const TIER_TONE: Record<(typeof TIERS)[number], Tone> = { strong: 'ok', normal: 'info', awkward: 'warn', blocked: 'danger' };
const TIER_COLOR: Record<(typeof TIERS)[number], string> = { strong: 'var(--ok)', normal: 'var(--info)', awkward: 'var(--warn)', blocked: 'var(--danger)' };

export function ContentPage() {
  const report = useContentReport();
  const [simKey, setSimKey] = useState<string | null>(null);
  return (
    <>
      <PageHeader title="劇本數據" description="內容版本狀況、每個劇本的遊玩數據，以及路線模擬器。" />
      <QueryView query={report} loading="Loading content report…">
        {(c) => (
          <>
            <ReleaseSummary c={c} />
            {c.issues.length > 0 && <IssuesCard issues={c.issues} />}
            <TemplatesCard
              templates={c.templates}
              selectedKey={simKey}
              onPick={(key) => {
                setSimKey(key);
                document.getElementById('simulator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          </>
        )}
      </QueryView>
      <div id="simulator">
        <Simulator templateKey={simKey} onTemplateChange={setSimKey} />
      </div>
    </>
  );
}

function ReleaseSummary({ c }: { c: ContentReport }) {
  const started = c.templates.reduce((n, t) => n + t.started, 0);
  const completed = c.templates.reduce((n, t) => n + t.completed, 0);
  return (
    <div className="row" style={{ gap: 'var(--sp-4)' }}>
      <span>
        內容版本 <code>{c.release}</code>
      </span>
      <span className="muted">{fmtInt(c.templates.length)} 個劇本</span>
      <span className="muted">
        開始 {fmtInt(started)} 次 · 完成率 {fmtPct(completed, started)}
      </span>
      {c.issues.length === 0 ? <Badge tone="ok">檢查通過</Badge> : <Badge tone="danger">{c.issues.length} 個檢查問題</Badge>}
    </div>
  );
}

function IssuesCard({ issues }: { issues: ContentReport['issues'] }) {
  return (
    <Card title="檢查問題" sub={`${issues.length}`} flush>
      <DataTable pageSize={20}
        rows={issues.map((x, i) => ({ ...x, i }))}
        rowKey={(x) => `${x.i}`}
        columns={[
          { key: 'path', header: '位置', render: (x) => <code>{x.path}</code>, sort: (x) => x.path },
          { key: 'message', header: '訊息', render: (x) => x.message },
        ]}
      />
    </Card>
  );
}

function OutcomeCell({ t }: { t: TemplateStats }) {
  const entries = Object.entries(t.outcomes);
  const total = entries.reduce((n, [, v]) => n + v, 0);
  if (entries.length === 0) return <span className="faint">—</span>;
  return (
    <div className="stack" style={{ gap: 4, minWidth: 180 }}>
      <Meter ariaLabel={`Outcome distribution for ${t.key}`} parts={entries.map(([k, v], i) => ({ key: k, value: v, color: seriesColor(i) }))} />
      <span className="small muted">
        {entries.map(([k, v], i) => (
          <span key={k} className="nowrap" style={{ marginRight: 10 }}>
            <span style={{ color: seriesColor(i) }}>●</span> {k} {fmtInt(v)}
            {total > 0 && <span className="faint"> ({fmtPct(v, total)})</span>}
          </span>
        ))}
      </span>
    </div>
  );
}

function TemplatesCard({ templates, selectedKey, onPick }: { templates: TemplateStats[]; selectedKey: string | null; onPick: (key: string) => void }) {
  const [filter, setFilter] = useState('');
  const f = filter.trim().toLowerCase();
  const rows = f ? templates.filter((t) => `${t.key} ${t.title} ${t.focusNpc ?? ''} ${t.kind}`.toLowerCase().includes(f)) : templates;
  return (
    <Card
      title="劇本"
      sub="點擊一行以模擬"
      flush
      actions={<input type="search" placeholder="篩選…" aria-label="篩選劇本" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 200, minHeight: 26 }} />}
    >
      <DataTable pageSize={20}
        rows={rows}
        rowKey={(t) => t.key}
        onRowClick={(t) => onPick(t.key)}
        selectedKey={selectedKey}
        initialSort={{ key: 'started', dir: 'desc' }}
        columns={[
          { key: 'key', header: 'Key', render: (t) => <code>{t.key}</code>, sort: (t) => t.key },
          { key: 'title', header: '標題', render: (t) => t.title, sort: (t) => t.title },
          { key: 'kind', header: '類型', render: (t) => humanize(t.kind), sort: (t) => t.kind },
          { key: 'npc', header: '主角', render: (t) => t.focusNpc ?? <span className="faint">—</span>, sort: (t) => t.focusNpc ?? '' },
          { key: 'beats', header: '幕數', align: 'right', render: (t) => t.beats, sort: (t) => t.beats },
          { key: 'started', header: '開始', align: 'right', render: (t) => fmtInt(t.started), sort: (t) => t.started },
          { key: 'completed', header: '完成', align: 'right', render: (t) => fmtInt(t.completed), sort: (t) => t.completed },
          { key: 'abandoned', header: '放棄', align: 'right', render: (t) => fmtInt(t.abandoned), sort: (t) => t.abandoned },
          { key: 'rate', header: '完成率', align: 'right', render: (t) => fmtPct(t.completed, t.started), sort: (t) => (t.started ? t.completed / t.started : -1) },
          { key: 'outcomes', header: '結局分佈', render: (t) => <OutcomeCell t={t} /> },
        ]}
      />
    </Card>
  );
}

// ---------- simulator ----------

function Simulator({ templateKey, onTemplateChange }: { templateKey: string | null; onTemplateChange: (key: string) => void }) {
  const templates = useTemplates();
  const simulate = useSimulate();
  const [gender, setGender] = useState<Gender>('male');
  const [statLevel, setStatLevel] = useState(30);
  const [met, setMet] = useState(true);

  const key = templateKey ?? templates.data?.[0]?.key ?? '';

  const run = (e: FormEvent) => {
    e.preventDefault();
    if (key) simulate.mutate({ key, gender, statLevel, met });
  };

  return (
    <Card title="路線模擬器" sub="以一個玩家設定走遍每條選擇路線">
      <div className="stack-lg">
        <form className="row" style={{ gap: 'var(--sp-4)', alignItems: 'flex-end' }} onSubmit={run}>
          <div style={{ flex: '1 1 260px', minWidth: 220 }}>
            <Field label="劇本">
              {(id) =>
                templates.isError ? (
                  <ErrorNote error={templates.error} />
                ) : (
                  <select id={id} value={key} onChange={(e) => onTemplateChange(e.target.value)} disabled={!templates.data}>
                    {templates.data?.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.key} — {t.title} ({t.kind})
                      </option>
                    ))}
                  </select>
                )
              }
            </Field>
          </div>
          <Field label="性別">{() => <Segmented label="性別" value={gender} options={[{ value: 'male', label: '男' }, { value: 'female', label: '女' }]} onChange={setGender} />}</Field>
          <Field label={`全部能力 = ${statLevel}`}>
            {(id) => (
              <div className="row" style={{ flexWrap: 'nowrap' }}>
                <input id={id} type="range" min={0} max={100} value={statLevel} onChange={(e) => setStatLevel(Number(e.target.value))} style={{ width: 140 }} />
                <input
                  type="number"
                  aria-label="能力值"
                  min={0}
                  max={100}
                  value={statLevel}
                  onChange={(e) => setStatLevel(Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))))}
                  style={{ width: 64 }}
                />
              </div>
            )}
          </Field>
          <div style={{ paddingBottom: 6 }}>
            <Checkbox checked={met} onChange={setMet}>
              已認識全部角色
            </Checkbox>
          </div>
          <Button type="submit" variant="primary" busy={simulate.isPending} disabled={!key}>
            模擬
          </Button>
        </form>
        {simulate.isError && <ErrorNote error={simulate.error} />}
        {simulate.data && <SimulationView r={simulate.data} build={simulate.variables} />}
      </div>
    </Card>
  );
}

function SimulationView({ r, build }: { r: SimulationResult; build: { gender: Gender; statLevel: number; met: boolean } | undefined }) {
  const unreachable = new Set(r.unreachable);
  const outcomeRows = Object.entries(r.outcomes).map(([id, n]) => ({ id, n }));
  const tierTotal = TIERS.reduce((n, t) => n + (r.tierMix[t] ?? 0), 0);
  return (
    <div className="stack-lg">
      <div className="row" style={{ gap: 'var(--sp-3)' }}>
        <strong>
          <code>{r.templateKey}</code>
        </strong>
        {build && (
          <span className="muted small">
            {build.gender === 'male' ? '男' : '女'}，能力 {build.statLevel}，{build.met ? '已認識全部角色' : '未認識角色'}
          </span>
        )}
        {r.availableForBuild ? <Badge tone="ok">這個設定可以玩</Badge> : <Badge tone="warn">未解鎖：{r.lock?.kind ?? 'unknown'}</Badge>}
        <Badge>
          {fmtInt(r.paths)} 條路線
          {r.paths > SIMULATION_PATH_CAP ? '（已達上限）' : ''}
        </Badge>
        {r.unreachable.length > 0 ? <Badge tone="danger">{r.unreachable.length} 個結局無法到達</Badge> : <Badge tone="ok">每個結局都能到達</Badge>}
      </div>

      {r.lock?.kind === 'condition' && (
        <Section title="未符合的前置條件">
          <JsonView value={r.lock.condition} />
        </Section>
      )}
      {r.lock && r.lock.kind !== 'condition' && (
        <p className="muted small">{r.lock.kind === 'slot' ? '模擬的時段不會出現。' : '已完成且不能重玩。'}路線仍然照樣模擬。</p>
      )}

      <div className="grid-2">
        <Section title="結局">
          <DataTable
            rows={outcomeRows}
            rowKey={(o) => o.id}
            initialSort={{ key: 'n', dir: 'desc' }}
            empty="這個劇本沒有結局。"
            columns={[
              {
                key: 'id',
                header: '結局',
                render: (o) =>
                  unreachable.has(o.id) ? (
                    <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                      <code>{o.id}</code> · unreachable
                    </span>
                  ) : (
                    <code>{o.id}</code>
                  ),
                sort: (o) => o.id,
              },
              { key: 'n', header: '路線', align: 'right', render: (o) => <span style={unreachable.has(o.id) ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>{fmtInt(o.n)}</span>, sort: (o) => o.n },
              { key: 'share', header: '比例', align: 'right', render: (o) => fmtPct(o.n, r.paths), sort: (o) => o.n },
            ]}
          />
        </Section>
        <Section title="判定結果分佈">
          <Meter ariaLabel="Tier mix" parts={TIERS.map((t) => ({ key: t, value: r.tierMix[t] ?? 0, color: TIER_COLOR[t] }))} />
          <KeyValues items={TIERS.map((t) => [t, `${fmtInt(r.tierMix[t] ?? 0)} (${fmtPct(r.tierMix[t] ?? 0, tierTotal)})`])} />
        </Section>
      </div>

      <Section title={`路線樣本（首 ${r.sample.length} 條）`}>
        <DataTable pageSize={20}
          rows={r.sample.map((p, i) => ({ ...p, i }))}
          rowKey={(p) => `${p.i}`}
          empty="這個設定沒有完整路線。"
          columns={[
            { key: 'i', header: '#', align: 'right', render: (p) => p.i + 1 },
            { key: 'outcome', header: '結局', render: (p) => <code>{p.outcome}</code>, sort: (p) => p.outcome },
            {
              key: 'path',
              header: '選擇（beat.choice → 判定）',
              render: (p) => (
                <span className="row" style={{ gap: 4 }}>
                  {p.path.map((step, j) => (
                    <span key={j} className="nowrap">
                      {j > 0 && <span className="faint">→ </span>}
                      <code>{step}</code> {p.tiers[j] && <Badge tone={TIER_TONE[p.tiers[j]]}>{p.tiers[j]}</Badge>}
                    </span>
                  ))}
                </span>
              ),
              sort: (p) => p.path.length,
            },
          ]}
        />
      </Section>
    </div>
  );
}
